import { Component, OnInit } from '@angular/core';

import { AppService } from '../../services/app.service';
import { ITableColumn } from '../../shared/table/table.component';
import { EmployeeCardService } from '../../services/employee-card.service';
import { EmployeeCardStatus, IEmployeeCardListItem } from '../../models/EmployeeCard';
import { ClassifierService } from '../../services/classifier.service';
import { debounceTime } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';

@Component({
    selector: 'app-employee-card-list',
    templateUrl: './list.component.html'
})
export class EmployeeCardListComponent implements OnInit {
    constructor(
        private app: AppService,
        private service: EmployeeCardService,
        private classifiers: ClassifierService,
        private route: ActivatedRoute
    ) { }

    items: IEmployeeCardListItem[] = [];

    readonly canEdit = this.app.currentUser.rights.includes('EMPLOYEE_CARD.EDIT');
    readonly canAdmin = this.app.currentUser.rights.includes('EMPLOYEE_CARD.ADMIN');

    readonly columns: ITableColumn[] = [
        { property: 'NameSurname', label: 'employeeCard_lblEmployee' },
        { property: 'Structure', label: 'employeeCard_lblStructure', cssClass: 'hidden-sm-down' },
        { property: 'Position', label: 'employeeCard_lblPosition', cssClass: 'hidden-md-down' },
        { property: 'Status', label: 'employeeCard_lblStatus', cssClass: 'hidden-sm-down' },
        { property: 'Modified', label: 'employeeCard_lblModified', type: 'date', cssClass: 'hidden-sm-down' },
        { width: '1px' }
    ];

    readonly filters: {
        status: {
            options: { text: string, value: EmployeeCardStatus }[], value: EmployeeCardStatus[]
        },
        reason: {
            options: { text: string, value: string }[], value: string[]
        },
        search?: string
    } = {
            status: { options: [], value: [] },
            reason: { options: [], value: [] },
            search: ''
        };

    searchValue: string;
    page: number = 1;
    pageSize: number = 10;
    total: number = 0;
    sortBy: string;
    sortDir: 'asc' | 'desc';
    hasActive: boolean;

    private isNoRedirect: boolean;
    private filterSubj = new Subject();

    ngOnInit() {
        if (this.canAdmin) {
            this.columns.find(t => t.property == 'NameSurname').sorts = true;
            this.columns.find(t => t.property == 'Structure').sorts = true;
            this.columns.find(t => t.property == 'Position').sorts = true;
            this.columns.find(t => t.property == 'Modified').sorts = true;

            this.pageSize = 10;

            this.filterSubj.pipe(debounceTime(300)).subscribe(() => {
                this.fetch(1);
            });

            this.filters.status.options = (['Draft', 'Submitted', 'Approved', 'Rejected', 'Revoked'] as EmployeeCardStatus[]).map(t => {
                return {
                    text: this.app.translate('employeeCard_status' + t),
                    value: t
                };
            });

            this.filters.status.value = ['Submitted'];

            this.app.addLoading(this.classifiers.get('EmployeeCardRevocationReason')).subscribe(data => {
                this.filters.reason.options = data.map(t => {
                    return {
                        text: t.Value,
                        value: t.Id
                    };
                });
            });
        } else {
            this.isNoRedirect = this.route.snapshot.queryParamMap.has('noRedirect');
            this.pageSize = 100;
        }

        this.fetch();
    }

    filter() {
        this.filterSubj.next();
    }

    sort(event: { sortBy: string, sortDir: 'asc' | 'desc' }) {
        this.sortBy = event.sortBy;
        this.sortDir = event.sortDir;

        this.fetch(1);
    }

    delete(item: IEmployeeCardListItem) {
        this.app.confirm(this.app.translate('confirmItemDelete'), result => {
            if (!result) return;

            this.app.addLoading(this.service.delete(item.Id)).subscribe(() => {
                this.fetch(1);
            });
        });
    }

    download(item: IEmployeeCardListItem) {
        this.app.addLoading(this.service.download(item.Id)).subscribe();
    }

    private fetch(page?: number) {
        if (!page) page = this.page;

        this.app.addLoading(this.service.get({
            page,
            size: this.pageSize,
            dir: this.sortDir,
            sort: this.sortBy
        }, {
            reasonId: this.filters.reason.value,
            search: this.filters.search,
            status: this.filters.status.value
        })).subscribe(data => {
            const active = data.items.find(t => (t.Status == 'Draft' || t.Status == 'Submitted' || t.Status == 'Approved')
                && t.Upn.toLowerCase() == this.app.currentUser.username.toLowerCase());

            if (!this.isNoRedirect) {
                if (active) {
                    this.app.navigate(['/employee-cards', active.Id]);
                    return;
                } else if (!this.canAdmin) {
                    this.app.navigate(['/employee-cards/add']);
                    return;
                }
            }

            this.page = page;
            this.items = data.items;
            this.total = data.total;
            this.hasActive = !!active;
        });
    }
}
